/**
 * dartFormProcessors.ts - DART Form-Specific Data Processing Utilities
 *
 * Contains functions for processing DART form template data into UI-ready structures.
 * These utilities are specific to DART forms (Escalations and Consultation).
 */

import {
  DropdownItem,
  Section,
  Question,
  QuestionOption,
  StepperBulletPointConfig,
} from './formData.types';
import { I18NContent } from '../../api/CoachingNotes/schema';
import {
  SECTION_IDS,
  DROPDOWN_TYPES,
  SECTION_NAME,
  TRANSLATION_IDS_SUBSTRING,
} from './formData.constants';

/**
 * Builds CSA (Customer Service Analytics) tracking fields for clickstream
 */
export const csaFields = (
  type: string,
  subtype: string,
  contentID: string,
  ingress: string | null,
  getFormClickstreamTag1AndTag2: (
    ingress: string | null,
    isCCX?: boolean
  ) => any,
  isCCX?: boolean
) =>
  ({
    'data-csa-c-type': type,
    'data-csa-c-slot-id': `'dart-review-${subtype}-button-slot'`,
    'data-csa-c-content-id': `dart-review-${subtype}-button-content-id-${contentID}`,
    ...getFormClickstreamTag1AndTag2(ingress, isCCX),
    'data-csa-c-tag3': `DART Review Form`,
  }) as const;

interface ReviewBarItem {
  meetsBar: I18NContent[];
  doesntMeetBar: I18NContent[];
  notApplicable: I18NContent[];
}

interface FormInformation {
  reviewBarCriteria: Record<string, ReviewBarItem>;
  stepperBulletPoints: Record<string, StepperBulletPointConfig>;
  defaultDropdownItems: DropdownItem[];
  tableHeaders: I18NContent[];
}

/**
 * Processes DART template data to extract dropdown items, criteria, and pages
 */
export const processTemplateData = (
  templateData: any
): Partial<FormInformation> => {
  if (!templateData) return {};

  const defaultDropdownItems =
    templateData.SECTIONS.find(
      (section: Section) => section.SECTION_ID === SECTION_IDS.DART_DROPDOWNS
    )?.QUESTIONS.map((question: Question) => ({
      type: question.QUESTION_ID.toLowerCase(),
      placeholder: question.QUESTION_TEXT,
      options: question.QUESTION_OPTIONS || [],
      selected: question?.DEFAULT_SELECTED?.DEFAULT_MESSAGE ?? undefined,
    })) || [];

  const tableHeaders: I18NContent[] =
    templateData.SECTIONS.find(
      (section: Section) => section.TYPE === SECTION_NAME.TABLE_HEADER
    )?.QUESTIONS.map((question: Question) => ({
      translationID: question.QUESTION_TEXT.TRANSLATION_ID,
      defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
    })) || [];

  const stepperBulletPoints = templateData.SECTIONS.filter(
    (section: Section) => section.TYPE === SECTION_NAME.TABLE
  ).reduce((acc: any, section: Section) => {
    if (section?.QUESTION_TEXT?.DEFAULT_MESSAGE) {
      const points = section.QUESTIONS.map((question: Question) => ({
        defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        translationID: question.QUESTION_TEXT.TRANSLATION_ID,
      }));
      acc[section.QUESTION_TEXT.DEFAULT_MESSAGE] = {
        points,
        trackLength:
          section.QUESTIONS.length <= 2
            ? 6
            : Math.ceil(section.QUESTIONS.length / 2) * 5,
        customClass: '',
      };
    }
    return acc;
  }, {});

  const reviewBarCriteria = templateData.SECTIONS.filter(
    ({ TYPE }: { TYPE: string }) => TYPE === SECTION_NAME.TABLE
  ).reduce((acc: any, section: Section) => {
    section.QUESTIONS.forEach(question => {
      if (
        section?.QUESTION_TEXT?.DEFAULT_MESSAGE &&
        question.QUESTION_OPTIONS
      ) {
        const meetsBar = question.QUESTION_OPTIONS.filter(
          (option: QuestionOption) =>
            option.TRANSLATION_ID.includes(TRANSLATION_IDS_SUBSTRING.MEETS_BAR)
        ).map(option => ({
          defaultMessage: option.DEFAULT_MESSAGE,
          translationID: option.TRANSLATION_ID,
        }));

        const doesntMeetBar = question.QUESTION_OPTIONS.filter(
          (option: QuestionOption) =>
            option.TRANSLATION_ID.includes(
              TRANSLATION_IDS_SUBSTRING.DOESNT_MEET_BAR
            )
        ).map(option => ({
          defaultMessage: option.DEFAULT_MESSAGE,
          translationID: option.TRANSLATION_ID,
        }));

        const notApplicable = question.QUESTION_OPTIONS.filter(
          (option: QuestionOption) =>
            option.TRANSLATION_ID.includes(
              TRANSLATION_IDS_SUBSTRING.NOT_APPLICABLE
            )
        ).map(option => ({
          defaultMessage: option.DEFAULT_MESSAGE,
          translationID: option.TRANSLATION_ID,
        }));

        acc[question.QUESTION_TEXT.DEFAULT_MESSAGE] = {
          meetsBar,
          doesntMeetBar,
          notApplicable,
        };
      }
    });
    return acc;
  }, {});

  return {
    defaultDropdownItems,
    stepperBulletPoints,
    reviewBarCriteria,
    tableHeaders,
  };
};

/**
 * Applies special rules to dropdown items for DART forms
 * - Sets form type dropdown from formType prop
 * - Sets vertical dropdown from selectedVertical prop
 */
export const applySpecialDropdownRules = (
  dropdownItems: DropdownItem[],
  formType: string,
  selectedVertical: string
): DropdownItem[] => {
  return dropdownItems.map(item => {
    // Set form type dropdown
    if (item.type.includes(DROPDOWN_TYPES.CONTACT_REVIEW_FORM_TYPE)) {
      return { ...item, selected: formType };
    }
    // Set vertical dropdown from prop
    if (item.type.includes(DROPDOWN_TYPES.VERTICAL)) {
      return { ...item, selected: selectedVertical };
    }
    return item;
  });
};
